import React, { useState, useRef, useEffect } from 'react';
import { useArmyDepartments } from '../hooks/useArmyDepartments';
import { useStaticRegionDistricts } from '../hooks/useRegionDistricts';
import { useDatepicker } from '../hooks/useDatepicker';
import { useRanks } from '../hooks/useRanks';
import ExcelUploadModal from './ExcelUploadModal';
import SignaturePad from './SignaturePad';
import WebcamCaptureModal from './WebcamCaptureModal';

import { t } from '../utils/i18n';
import { parseApiErrors, validatePersonnelForm } from '../utils/formatApiErrors';



const FieldError = ({ name, fieldErrors }) => {
  if (!fieldErrors[name]) return null;
  return (
    <div className="personnel-field-error" role="alert" style={{
      color: '#dc3545',
      fontSize: '12px',
      marginTop: '4px',
      display: 'flex',
      alignItems: 'center',
      gap: '4px'
    }}>
      <span aria-hidden="true">✕</span>
      {fieldErrors[name]}
    </div>
  );
};

const ValidationAlert = ({ messages, bannerRef }) => {
  if (!messages || messages.length === 0) return null;
  return (
    <div ref={bannerRef} className="personnel-validation-banner" role="alert" aria-live="assertive" style={{
      background: '#fff5f5',
      border: '1px solid #f5c6cb',
      borderLeft: '4px solid #dc3545',
      borderRadius: '6px',
      padding: '14px 16px',
      marginBottom: '20px',
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
        <span style={{ fontSize: '18px', lineHeight: 1, marginTop: '1px' }}>⚠️</span>
        <div style={{ flex: 1 }}>
          <strong style={{ display: 'block', color: '#721c24', fontSize: '14px', marginBottom: '6px' }}>
            Registration failed — please fix the following:
          </strong>
          <ul style={{ margin: 0, paddingLeft: '18px', listStyle: 'disc' }}>
            {messages.map((msg, index) => (
              <li key={`${msg}-${index}`} style={{ color: '#721c24', fontSize: '13px', marginBottom: '2px' }}>
                {msg}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

const invalidFieldStyle = (name, fieldErrors) =>
  fieldErrors[name] ? { borderColor: '#c0392b', boxShadow: '0 0 0 1px #c0392b' } : undefined;

const FingerprintImageCanvas = ({ imageData, width, height }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    if (!imageData || !width || !height || !canvasRef.current) return;

    try {
      // Decode the base64 image data
      const binaryString = atob(imageData);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }

      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');

      // Set canvas size
      canvas.width = width;
      canvas.height = height;

      // Create image data
      const imageDataObj = ctx.createImageData(width, height);

      // Fill with grayscale pixels (ZKTeco returns 8-bit grayscale)
      for (let i = 0; i < bytes.length && i < width * height; i++) {
        const gray = bytes[i];
        const pixelIndex = i * 4;
        imageDataObj.data[pixelIndex] = gray;     // R
        imageDataObj.data[pixelIndex + 1] = gray; // G
        imageDataObj.data[pixelIndex + 2] = gray; // B
        imageDataObj.data[pixelIndex + 3] = 255;  // A
      }

      // Put the image data to canvas
      ctx.putImageData(imageDataObj, 0, 0);
    } catch (error) {
      console.error('Error rendering fingerprint image:', error);
    }
  }, [imageData, width, height]);

  // Calculate display size (max 200x150 while maintaining aspect ratio)
  const aspectRatio = width / height;
  let displayWidth = Math.min(200, width);
  let displayHeight = displayWidth / aspectRatio;

  if (displayHeight > 150) {
    displayHeight = 150;
    displayWidth = displayHeight * aspectRatio;
  }

  return (
    <canvas
      ref={canvasRef}
      style={{
        width: `${displayWidth}px`,
        height: `${displayHeight}px`,
        border: '2px solid #4caf50',
        borderRadius: '4px',
        background: '#fff',
        imageRendering: 'pixelated'
      }}
      alt="Captured Fingerprint"
    />
  );
};

// Estimate sensible canvas dimensions for a raw fingerprint byte array
const estimateFingerprintDims = (base64) => {
  try {
    const raw = atob(base64);
    const len = raw.length;
    // Prefer a common width, fall back to square-ish estimate
    let width = 256;
    let height = Math.ceil(len / width);
    if (height > 1000 || height < 4) {
      width = Math.max(64, Math.floor(Math.sqrt(len)));
      height = Math.ceil(len / width);
    }
    return { width, height };
  } catch (e) {
    return { width: 256, height: 256 };
  }
};

const AddPersonnel = ({ onBack, onNavigate, api }) => {
  useDatepicker();

  const [formData, setFormData] = useState({
    fullName: '',
    gender: 'Male',
    maritalStatus: 'Single',
    educationalLevel: "High School",
    bloodType: 'O+',
    birthDate: '',
    phone: '',
    email: '',
    emergencyContact: '',
    armyType: '',
    salaryUsd: '',
    address: '',
    allowanceUsd: '',
    bankAccount: '',
    bankName: '',
    emergencyContactPhone: '',
    emergencyContactRelation: '',
    responsiblePersonName: '',
    responsiblePersonPhone: '',
    training: '',
    motherName: '',
    remarks: '',
    armyDepartment: '',
    subDepartment: '',
    rank: '',
    armyEquipment: '',
    district: '',
    region: '',
    station: '',
    documents: [],
    biometricEnrolledCount: 0,
    armyPhoto: '',
    status: '',
    title: '',
    placeOfBirth: '',
    previousData: '',
    organization: '',
    workPlace: '',
    biometricSignature: ''
  });

  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState('');
  const [showWebcamModal, setShowWebcamModal] = useState(false);
  const [signature, setSignature] = useState('');
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMessages, setErrorMessages] = useState([]);
  const [fieldErrors, setFieldErrors] = useState({});
  const [showEnrollModal, setShowEnrollModal] = useState(false);
  const [captureLoading, setCaptureLoading] = useState(false);
  const [captureStatus, setCaptureStatus] = useState('');
  const [capturedFingerprintData, setCapturedFingerprintData] = useState(null);
  const [availableDevices, setAvailableDevices] = useState([]);
  const [selectedBiometricDeviceId, setSelectedBiometricDeviceId] = useState('');

  // SpeedFace-V5L face capture state
  const [faceCapturing, setFaceCapturing] = useState(false);
  const [faceStatus, setFaceStatus] = useState('');
  const [capturedFaceData, setCapturedFaceData] = useState(null); // { photo, template }
  const [capturedFpFromDevice, setCapturedFpFromDevice] = useState(null); // fingerprint from device
  const [speedfaceDevice, setSpeedfaceDevice] = useState('');
  const [availableSpeedfaceDevices, setAvailableSpeedfaceDevices] = useState([]);
  const [scanStep, setScanStep] = useState('idle'); // idle | waiting | done
  const [devicePin, setDevicePin] = useState('');
  const [duplicateFace, setDuplicateFace] = useState(null);
  const [captureAttempts, setCaptureAttempts] = useState(0);
  const facePollingRef = useRef(null);
  const errorBannerRef = useRef(null);

  const [signatureStatus, setSignatureStatus] = useState('');

  const { armyTypes, filteredDepartments, filteredSubDepartments, handleArmyTypeChange, handleDepartmentChange, toSomali } = useArmyDepartments(api);
  const { regions, filteredDistricts, handleRegionChange } = useStaticRegionDistricts();
  const { ranks } = useRanks(api);
  const [showExcelModal, setShowExcelModal] = useState(false);

  // jQuery UI selectmenu breaks React controlled selects — keep native dropdowns on this form.
  useEffect(() => {
    const destroySelectmenu = () => {
      if (!window.$?.fn?.selectmenu) return;
      window.$('#personnelForm select').each(function () {
        const $el = window.$(this);
        try {
          if ($el.selectmenu('instance')) {
            $el.selectmenu('destroy');
          }
        } catch (_) {
          /* selectmenu not initialized on this element */
        }
      });
    };

    destroySelectmenu();
    const timer = setTimeout(destroySelectmenu, 250);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const loadBiometricDevices = async () => {
      try {
        const result = await api.get('/biometric_devices');
        const devices = (result && result.data && result.data.biometric_devices) || [];
        setAvailableDevices(Array.isArray(devices) ? devices : []);
        if (devices && devices.length > 0) {
          setSelectedBiometricDeviceId(devices[0].id);
        }
      } catch (err) {
        console.error('Failed to load biometric devices', err);
      }
    };

    loadBiometricDevices();
  }, []);

  const clearFieldError = (name) => {
    setFieldErrors((prev) => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  };

  const applyValidationErrors = ({ fieldErrors: nextFieldErrors, messages }) => {
    setFieldErrors(nextFieldErrors || {});
    setErrorMessages(messages || []);
    requestAnimationFrame(() => {
      errorBannerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  };

  const handleChangeArmyType = (e) => {
    const { name, value } = e.target;
    clearFieldError(name);
    if (name === 'armyDepartment') clearFieldError('armyDepartment');
    if (name === 'armyType') {
      handleArmyTypeChange(value);
      setFormData({ ...formData, [name]: value, armyDepartment: '', subDepartment: '' });
    } else if (name === 'armyDepartment') {
      handleDepartmentChange(value);
      setFormData({ ...formData, [name]: value, subDepartment: '' });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleChangeRegion = (e) => {
    const { name, value } = e.target;
    clearFieldError(name);
    if (name === 'region') clearFieldError('district');

    if (name === 'region') {
      handleRegionChange(value);
      setFormData({ ...formData, [name]: value, district: '' });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };


  const handlePhotoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPhoto(file);
      const reader = new FileReader();
      reader.onload = (ev) => {
        setPhotoPreview(ev.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleWebcamPhotoCapture = (dataUrl) => {
    // Set photo preview and photo state for Army Photo ONLY
    setPhotoPreview(dataUrl);
    setPhoto({ name: `webcam_photo_${Date.now()}.jpg`, type: 'image/jpeg' });
    setShowWebcamModal(false);
  };

  const handleClearPhoto = (e) => {
    e.stopPropagation();
    setPhoto(null);
    setPhotoPreview('');
    const input = document.getElementById('photo-upload');
    if (input) input.value = '';
  };
  const handleDocumentUpload = (e) => {
    setDocuments([...e.target.files]);
  };

  // SpeedFace-V5L: capture face photo via device local HTTP API
  // The device exposes GET http://<ip>/iclock/getuser?uid=<uid> and a capture endpoint.
  // For enrollment capture we use the /iclock/cdata endpoint or the ZKBio push.
  // Most accessible approach: GET http://<ip>/api/face/capture (ZKTeco local REST)
  const handleFaceCapture = async () => {
    if (!speedfaceDevice) { setFaceStatus('Please select a device first.'); return; }
    if (!devicePin) { setFaceStatus('Please enter the device PIN first.'); return; }

    clearInterval(facePollingRef.current);
    setFaceCapturing(true);
    setScanStep('waiting');
    setCapturedFaceData(null);
    setCapturedFpFromDevice(null);
    setDuplicateFace(null);
    setCaptureAttempts(0);
    setFaceStatus('Querying device for PIN ' + devicePin + '...');

    // Queue a PIN-specific BIODATA query on the device
    await api.get(`/speedface/capture_face?device_sn=${encodeURIComponent(speedfaceDevice)}&pin=${encodeURIComponent(devicePin)}`);

    let attempts = 0;
    facePollingRef.current = setInterval(async () => {
      attempts++;
      setCaptureAttempts(attempts);

      try {
        const result = await api.get(
          `/speedface/pending?device_sn=${encodeURIComponent(speedfaceDevice)}&pin=${encodeURIComponent(devicePin)}`
        );
        const data = result?.data;

        if (data?.found) {
          clearInterval(facePollingRef.current);

          if (data.duplicate) {
            setScanStep('idle');
            setDuplicateFace(data.duplicate);
            setFaceStatus(`⚠️ Already registered as: ${data.duplicate.name} (ID: ${data.duplicate.id}). Blocked.`);
            setFaceCapturing(false);
            return;
          }

          // Store face (photo + template) and fingerprint if returned
          setCapturedFaceData({ template: data.template || null, photo: data.photo || null });
          if (data.fingerprint) {
            setCapturedFpFromDevice({ template: data.fingerprint });
            setCapturedFingerprintData({ template: data.fingerprint });
          }

          setScanStep('done');
          setFaceCapturing(false);
          const captured = [];
          if (data.photo || data.template) captured.push('face');
          if (data.fingerprint) captured.push('fingerprint');
          setFaceStatus(`✓ Captured: ${captured.join(' + ')} from device (PIN: ${data.pin}).`);
        } else if (attempts >= 24) {
          clearInterval(facePollingRef.current);
          setScanStep('idle');
          setFaceCapturing(false);
          setFaceStatus('Timed out (120s). Ensure PIN ' + devicePin + ' is enrolled on the device.');
        } else {
          setFaceStatus(`Waiting for device... ${attempts * 5}s / 120s (PIN: ${devicePin})`);
        }
      } catch (err) {
        console.error('Polling error', err);
      }
    }, 5000);
  };

  // Cleanup polling on unmount
  useEffect(() => () => clearInterval(facePollingRef.current), []);

  // Load speedface devices for dropdown
  useEffect(() => {
    api.get('/biometric_devices').then(result => {
      const devices = result?.data?.biometric_devices || [];
      setAvailableSpeedfaceDevices(devices);
      // Do not auto-select — user must choose the device
    }).catch(() => { });
  }, []);

  // Fallback: capture face from webcam via hidden video element
  const webcamRef = useRef(null);
  const webcamStreamRef = useRef(null);
  const [showWebcam, setShowWebcam] = useState(false);

  const startWebcam = async () => {
    setShowWebcam(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      webcamStreamRef.current = stream;
      if (webcamRef.current) webcamRef.current.srcObject = stream;
    } catch (err) {
      setFaceStatus('Webcam not available: ' + err.message);
      setShowWebcam(false);
    }
  };

  const captureFromWebcam = () => {
    const video = webcamRef.current;
    if (!video) return;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0);
    const base64 = canvas.toDataURL('image/jpeg').split(',')[1];
    setCapturedFaceData(base64);
    setFaceStatus('Face captured from webcam.');
    stopWebcam();
  };

  const stopWebcam = () => {
    if (webcamStreamRef.current) {
      webcamStreamRef.current.getTracks().forEach(t => t.stop());
      webcamStreamRef.current = null;
    }
    setShowWebcam(false);
  };

  const openEnrollModal = () => {
    setShowEnrollModal(true);
    setCaptureStatus('');
    setCapturedFingerprintData('');
  };

  const closeEnrollModal = () => {
    setShowEnrollModal(false);
    setCaptureStatus('');
  };

  const captureFingerprintFromLocalService = async () => {
    if (window.zkCaptureFingerprint) {
      return await window.zkCaptureFingerprint();
    }

    const localEndpoints = [
      'http://127.0.0.1:5000/capture_fingerprint',
      'http://127.0.0.1:4000/capture_fingerprint',
      'http://localhost:5000/capture_fingerprint',
      'http://localhost:4000/capture_fingerprint'
    ];

    for (const endpoint of localEndpoints) {
      try {
        const response = await fetch(endpoint, { method: 'GET' });
        if (!response.ok) continue;
        const json = await response.json();
        if (json && json.data) {
          return json.data;
        }
      } catch (err) {
        // ignore and try next endpoint
      }
    }

    throw new Error('Unable to contact local fingerprint capture service. Install the ZKTeco helper or provide a browser-compatible capture bridge.');
  };

  const handleFingerprintCapture = async () => {
    setCaptureLoading(true);
    setCaptureStatus('Waiting for the fingerprint scanner...');

    try {
      const capturedData = await captureFingerprintFromLocalService();
      if (!capturedData || !capturedData.template) {
        throw new Error('No fingerprint data returned from device');
      }

      setCapturedFingerprintData(capturedData);
      setFormData({ ...formData, biometricEnrolledCount: Math.max(1, Number(formData.biometricEnrolledCount) || 0) });
      setCaptureStatus('Fingerprint captured successfully.');
    } catch (err) {
      console.error('Fingerprint capture failed', err);
      setCaptureStatus(err.message || 'Fingerprint capture failed');
    } finally {
      setCaptureLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (duplicateFace) {
      applyValidationErrors({
        fieldErrors: {},
        messages: [`Registration blocked: face already registered as ${duplicateFace.name} (ID: ${duplicateFace.id})`]
      });
      return;
    }

    const clientValidation = validatePersonnelForm(formData);
    if (clientValidation.messages.length > 0) {
      applyValidationErrors(clientValidation);
      return;
    }

    setLoading(true);
    setErrorMessages([]);
    setFieldErrors({});

    // Prepare optional file payloads
    let finalSignature = signature;
    if (!finalSignature && canvasRef.current && !isCanvasBlank(canvasRef.current)) {
      finalSignature = trimSignatureCanvas(canvasRef.current);
    }
    const photoPayload = photoPreview ? photoPreview.split(',')[1] || photoPreview : undefined;
    const signaturePayload = signature ? signature : undefined;

    // Read document files as base64
    const documentsPayload = await Promise.all(
      documents.map(file => new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (ev) => resolve({
          name: file.name,
          doc_type: file.type,
          data: ev.target.result.split(',')[1]
        });
        reader.readAsDataURL(file);
      }))
    );

    try {
      // Create the payload object
      const payload = {
        army_personnel: {
          full_name: formData.fullName,
          gender: formData.gender,
          marital_status: formData.maritalStatus,
          educational_level: formData.educationalLevel,
          blood_type: formData.bloodType,
          birth_date: formData.birthDate,
          phone: formData.phone,
          email: formData.email,
          emergency_contact: formData.emergencyContact,
          army_type_id: formData.armyType,
          salary_usd: formData.salaryUsd,
          address: formData.address,
          allowance_usd: formData.allowanceUsd,
          bank_account: formData.bankAccount,
          bank_name: formData.bankName,
          emergency_contact_phone: formData.emergencyContactPhone,
          emergency_contact_relation: formData.emergencyContactRelation,
          responsible_person_name: formData.responsiblePersonName,
          responsible_person_phone: formData.responsiblePersonPhone,
          training: formData.training,
          mother_name: formData.motherName,
          remarks: formData.remarks,
          department_id: formData.armyDepartment,
          sub_department_id: formData.subDepartment,
          rank: formData.rank,
          army_equipment: formData.armyEquipment,
          district: formData.district,
          region: formData.region,
          station: formData.station,
          status: formData.status,
          prev_data: formData.previousData,
          title: formData.title,
          place_of_birth: formData.placeOfBirth,
          registeredLocation: formData.registeredLocation,
          organization: formData.organization,
          work_place: formData.workPlace,
          biometric_enrolled_count: formData.biometricEnrolledCount,
          // Attach uploaded assets as data URLs
          army_photo: photoPayload,
          documents: documentsPayload.length > 0 ? documentsPayload : (formData.documents || []),
          // include signature as a separate field (also keep it in documents if desired)
          biometric_signature: signaturePayload
        }
      };

      const { response, data } = await api.post('/army_personnel', payload);

      if (response.ok) {
        const personnelId = data?.data?.army_personnel?.id;

        // Save face biometric (non-blocking — don't fail the whole submit)
        if (personnelId && capturedFaceData) {
          try {
            await api.post('/biometric_data', {
              army_personnel_id: personnelId,
              biometric_type: 'photo',
              data: capturedFaceData.photo || capturedFaceData.template,
              raw_template: `DEVPIN:${speedfaceDevice}:${devicePin}:${capturedFaceData.template || ''}`
            });
            api.delete(`/speedface/pending?device_sn=${encodeURIComponent(speedfaceDevice)}`).catch(() => { });
          } catch (faceErr) {
            console.warn('Face biometric save failed (non-critical):', faceErr);
          }
        }

        let enrollmentFailed = false;

        if (personnelId && capturedFingerprintData && capturedFingerprintData.template) {
          const deviceId = selectedBiometricDeviceId || (availableDevices[0] && availableDevices[0].id);

          if (!deviceId) {
            console.warn('Fingerprint enrollment skipped: No biometric device selected');
            enrollmentFailed = false; // Don't fail, just skip enrollment
          } else {
            const biometricPayload = {
              army_personnel_id: personnelId,
              biometric_device_id: deviceId,
              biometric_type: 'fingerprint',
              data: capturedFingerprintData.image || capturedFingerprintData.template,
              raw_template: capturedFingerprintData.template // Store raw template for verification
            };

            try {
              const enrollResult = await api.post('/biometric_data', biometricPayload);
              if (enrollResult.response && enrollResult.response.ok) {
                console.log('✓ Fingerprint enrollment successful', enrollResult.data);
              } else {
                console.warn('✗ Fingerprint enrollment failed', enrollResult.data || enrollResult);
                setErrorMessages([`Fingerprint enrollment failed: ${enrollResult.data?.error || 'Unknown error'}`]);
                enrollmentFailed = true;
              }
            } catch (enrollErr) {
              console.error('✗ Fingerprint enrollment error:', enrollErr);
              setErrorMessages([`Fingerprint enrollment error: ${enrollErr.message}`]);
              enrollmentFailed = true;
            }
          }
        } else {
          if (!capturedFingerprintData?.template) {
            console.log('ℹ Fingerprint enrollment skipped: No fingerprint captured');
          }
        }

        if (enrollmentFailed) {
          console.error('❌ Enrollment failed, personnel not saved to list view');
          // Don't navigate on failure - let user retry or fix the issue
        } else {
          console.log('✓ Personnel and biometric data saved successfully');
          // Redirect to the appropriate personnel list based on army type.
          // Try to resolve the army type name from the selected form value or
          // from the created personnel payload (server response).
          try {
            const createdArmyTypeName = data?.data?.army_personnel?.army_type?.name;
            const selectedType = armyTypes.find(at => String(at.id) === String(formData.armyType));
            const armyTypeName = (createdArmyTypeName || selectedType?.name || '').toString().toLowerCase();

            let target = 'personnel';
            if (/police/i.test(armyTypeName)) target = 'personnel-police';
            else if (/milit|darawiish|military/i.test(armyTypeName)) target = 'personnel-military';
            else if (/discipline|custodial|asluubta/i.test(armyTypeName)) target = 'personnel-discipline';

            onNavigate(target);
          } catch (e) {
            // fallback to generic personnel list on any unexpected error
            onNavigate('personnel');
          }
        }
      } else {
        const errorPayload = data?.errors || data?.error ? data : (data?.data || data);
        applyValidationErrors(parseApiErrors(errorPayload));
      }
    } catch (err) {
      setErrorMessages(['Network error — please check your connection and try again.']);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    clearFieldError(name);
    setFormData({ ...formData, [name]: value });
  };

  return (
    <>

      <div className="mainContent mainContentRg">
        <div className="mainContentHD mb_48 d-flex justify-content-between">
          <div>
            <h1 className="txt24 fwSemiBold colorBlack mb_8" data-i18n="armyRegistration">
              {t('armyRegistration') || 'Army Registration'}
            </h1>
            <p data-i18n="recordBasicInfo">
              {t('recordBasicInfo') || 'Record basic info, contacts, photo, biometric and signature'}
            </p>
          </div>

          <div>
            <button className="btn btn-success" onClick={() => setShowExcelModal(true)}>
              Import Excel
            </button>
          </div>
        </div>

        <ValidationAlert messages={errorMessages} bannerRef={errorBannerRef} />

        <div className="mainContentCT">
          <div className="mainContentRgLft">
            <div className="upldSec">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                <h2 className="txt20 fwMedium colorBlack m-0" data-i18n="armyPhoto">
                  {t('armyPhoto') || 'Army Photo'}
                </h2>
                {photoPreview && (
                  <span style={{ fontSize: '11px', background: '#e0f2fe', color: '#0369a1', padding: '2px 8px', borderRadius: '12px', fontWeight: 600 }}>
                    ✓ Ready for ID Card
                  </span>
                )}
              </div>

              <div
                className={`upload-wrapper ${photoPreview ? 'hasImg' : ''}`}
                style={{ cursor: 'pointer', position: 'relative' }}
                onClick={() => {
                  if (!photoPreview) {
                    setShowWebcamModal(true);
                  }
                }}
              >
                {!photoPreview ? (
                  <label className="upload-label" style={{ cursor: 'pointer' }}>
                    <img src="/images/cam.png" alt="" />
                    <span id="photoStatus" data-i18n="noPhotoSelected" style={{ fontSize: '13px', textAlign: 'center', color: '#64748b' }}>
                      {photo ? photo.name : (t('clickToCaptureOrUpload', 'Capture with Webcam or Upload'))}
                    </span>
                  </label>
                ) : (
                  <>
                    <img id="preview" className="preview" src={photoPreview} alt="Preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    <button
                      type="button"
                      className="army-photo-corner-remove"
                      onClick={handleClearPhoto}
                      title="Remove photo"
                    >
                      ✕
                    </button>
                  </>
                )}

                <input
                  type="file"
                  id="photo-upload"
                  accept="image/*"
                  onChange={handlePhotoUpload}
                  style={{ display: 'none' }}
                />
              </div>

              <div className="army-photo-actions-wrap">
                <button
                  type="button"
                  className="btn army-photo-webcam-btn"
                  onClick={() => setShowWebcamModal(true)}
                  title="Capture live photo with webcam"
                >
                  📷 {photoPreview ? (t('retakeWebcam', 'Retake')) : (t('takePhotoWebcam', 'Use Webcam'))}
                </button>

                <button
                  type="button"
                  className="btn inv army-photo-upload-btn"
                  onClick={() => document.getElementById('photo-upload').click()}
                  title="Upload from computer"
                >
                  📁 {photoPreview ? (t('changeFile', 'Change')) : (t('upload', 'Upload'))}
                </button>
              </div>
            </div>

            {/* SpeedFace-V5L Face Capture Section */}
            <div className="upldSec">
              <h2 className="txt20 fwMedium colorBlack mb_10">Face Capture (SpeedFace-V5L)</h2>

              {/* Step instructions */}
              <div style={{ background: '#f0f7ff', borderRadius: '6px', padding: '10px 12px', marginBottom: '10px', fontSize: '12px', color: '#333', lineHeight: '1.6' }}>
                <strong>How to capture face biometric:</strong><br />
                1. Enroll person on device (Menu → User Mgt → New User)<br />
                2. Make sure to add face, finger and profile photo<br />
                3. Note the ID used on device (e.g. 1,3,...)<br />
                4. Enter PIN below and click Capture — face template will be saved
              </div>

              {/* Device selector */}
              <div className="formInpt mb_10">
                <label className="mb_6" style={{ fontSize: '13px' }}>Select Device</label>
                {availableSpeedfaceDevices.length > 0 ? (
                  <select
                    value={speedfaceDevice}
                    onChange={e => setSpeedfaceDevice(e.target.value)}
                    style={{ height: '36px', fontSize: '13px', width: '100%', padding: '0 8px', border: '1px solid #ccc', borderRadius: '6px' }}
                  >
                    <option value="">-- Select Device --</option>
                    {availableSpeedfaceDevices.map(d => (
                      <option key={d.id} value={d.device_id}>{d.device_id} — {d.model}</option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    value={speedfaceDevice}
                    onChange={e => setSpeedfaceDevice(e.target.value)}
                    placeholder="e.g. SYZ8243601042"
                    style={{ height: '36px', fontSize: '13px' }}
                  />
                )}
              </div>

              {/* Device PIN input */}
              <div className="formInpt mb_10">
                <label className="mb_6" style={{ fontSize: '13px' }}>Device PIN (assigned on device)</label>
                <input
                  type="text"
                  value={devicePin}
                  onChange={e => setDevicePin(e.target.value)}
                  placeholder="e.g. 3"
                  style={{ height: '36px', fontSize: '13px' }}
                />
              </div>

              {/* Face preview */}
              <div
                className="upload-wrapper"
                style={{ height: '180px', cursor: 'default', marginBottom: '8px' }}
              >
                {capturedFaceData ? (
                  capturedFaceData.photo ? (
                    <img
                      src={capturedFaceData.photo.startsWith('data:') ? capturedFaceData.photo : `data:image/jpeg;base64,${capturedFaceData.photo}`}
                      alt="Captured Face"
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : (
                    <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#f0f7ff' }}>
                      <span style={{ fontSize: '30px' }}>✓</span>
                      <span style={{ fontSize: '13px', color: '#2e7d32', fontWeight: 600, marginTop: '8px' }}>Face Template Captured</span>
                      <span style={{ fontSize: '11px', color: '#888', marginTop: '4px' }}>{capturedFaceData.template?.length} bytes · PIN {devicePin}</span>
                    </div>
                  )
                ) : showWebcam ? (
                  <video ref={webcamRef} autoPlay playsInline style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  <label className="upload-label">
                    <img src="/images/cam.png" alt="" />
                    <span style={{ fontSize: '13px' }}>No face captured</span>
                  </label>
                )}
              </div>

              {/* Captured data summary — ✓/✗ per item */}
              {capturedFaceData && (() => {
                const hasPhoto = !!capturedFaceData.photo;
                const hasTemplate = !!capturedFaceData.template;
                const hasFp = !!capturedFpFromDevice;
                const items = [
                  { label: 'Face Photo', ok: hasPhoto, detail: null },
                  { label: 'Face Template', ok: hasTemplate, detail: hasTemplate ? `${capturedFaceData.template.length} bytes` : null },
                  { label: 'Fingerprint', ok: hasFp, detail: null },
                ];
                return (
                  <div style={{ border: '1px solid #e0e0e0', borderRadius: '8px', overflow: 'hidden', marginBottom: '8px' }}>
                    {items.map(({ label, ok, detail }) => (
                      <div key={label} style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '7px 12px',
                        borderBottom: '1px solid #f0f0f0',
                        background: ok ? '#f6fff6' : '#fff8f8'
                      }}>
                        <span style={{ fontSize: '12px', color: '#444' }}>
                          {label}{detail && <span style={{ color: '#999', marginLeft: '6px' }}>({detail})</span>}
                        </span>
                        <span style={{
                          fontSize: '13px', fontWeight: 700,
                          color: ok ? '#2e7d32' : '#c62828'
                        }}>
                          {ok ? '✓' : '✗'}
                        </span>
                      </div>
                    ))}
                    <div style={{ padding: '5px 12px', background: '#f9f9f9', fontSize: '11px', color: '#888' }}>
                      PIN: {devicePin} · {speedfaceDevice}
                    </div>
                  </div>
                );
              })()}

              {/* Duplicate warning */}
              {duplicateFace && (
                <div style={{ background: '#fff3e0', border: '1px solid #ff9800', borderRadius: '6px', padding: '10px 12px', marginBottom: '8px', fontSize: '12px' }}>
                  <strong style={{ color: '#e65100' }}>⚠️ Duplicate Detected</strong><br />
                  Already registered as: <strong>{duplicateFace.name}</strong> (ID: {duplicateFace.id})<br />
                  <span style={{ color: '#888' }}>Registration is blocked. Use a different PIN or device.</span>
                </div>
              )}

              {/* Status */}
              {faceStatus && (
                <p style={{ fontSize: '12px', color: duplicateFace ? '#e65100' : capturedFaceData ? '#4caf50' : '#555', marginBottom: '8px' }}>
                  {faceCapturing && <span style={{ marginRight: '6px' }}>⏳</span>}
                  {faceStatus}
                  {faceCapturing && captureAttempts > 0 && (
                    <span style={{ color: '#aaa', marginLeft: '6px' }}>({captureAttempts * 5}s)</span>
                  )}
                </p>
              )}

              {/* Actions */}
              {!capturedFaceData && !showWebcam && (
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    type="button"
                    className="btn inv"
                    style={{ flex: 1, fontSize: '13px', minHeight: '36px' }}
                    onClick={handleFaceCapture}
                    disabled={faceCapturing}
                  >
                    {faceCapturing ? `Waiting... (${captureAttempts * 5}s)` : '📷 Capture from Device'}
                  </button>
                  <button
                    type="button"
                    className="btn inv"
                    style={{ flex: 1, fontSize: '13px', minHeight: '36px' }}
                    onClick={startWebcam}
                  >
                    🎥 Webcam
                  </button>
                </div>
              )}

              {showWebcam && (
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button type="button" className="btn" style={{ flex: 1, fontSize: '13px', minHeight: '36px' }} onClick={captureFromWebcam}>📸 Snap</button>
                  <button type="button" className="btn inv" style={{ flex: 1, fontSize: '13px', minHeight: '36px' }} onClick={stopWebcam}>Cancel</button>
                </div>
              )}

              {capturedFaceData && (
                <button
                  type="button"
                  className="btn inv fullWidth mt_8"
                  style={{ fontSize: '13px', minHeight: '36px' }}
                  onClick={() => { setCapturedFaceData(null); setCapturedFpFromDevice(null); setFaceStatus(''); setDuplicateFace(null); setScanStep('idle'); }}
                >
                  🔄 Retake
                </button>
              )}
            </div>

            <div className="upldSec">
              <SignaturePad
                value={signature}
                onChange={(sigData) => setSignature(sigData)}
              />
            </div>

            <div className="upldSec">
              <h2 className="txt20 fwMedium colorBlack mb_10" data-i18n="documentsUpload">
                {t('documentsUpload') || 'Documents Upload'}
              </h2>
              <input
                type="file"
                id="documents"
                multiple
                accept=".pdf,.doc,.docx,.jpg,.png"
                className="customFileInpt"
                onChange={handleDocumentUpload}
              />
            </div>
          </div>

          <form id="personnelForm" className="mainContentRgRgt" onSubmit={handleSubmit} noValidate>
            <div className="formBlockMain">
              <div className="mb_18 formBlock">
                <h2 className="txt20 fwMedium colorBlack" data-i18n="personelDetails">
                  {t('personelDetails') || 'Personal Details'}
                </h2>
                <ul className="dFlex pt_8 pb_8 formFieldsWrap">
                  <li className="w_1_3">
                    <div className="formInpt">
                      <label className="mb_10" htmlFor="fullName" data-i18n="fullName">
                        {t('fullName') || 'Full Name'}
                      </label>
                      <input
                        id="fullName"
                        name="fullName"
                        type="text"
                        placeholder="John Parker"
                        value={formData.fullName}
                        onChange={handleChange}
                        style={invalidFieldStyle('fullName', fieldErrors)}
                      />
                      <FieldError name="fullName" fieldErrors={fieldErrors} />
                    </div>
                  </li>
                  <li className="w_1_3">
                    <div className="formInpt">
                      <label className="mb_10" htmlFor="motherName" data-i18n="motherName">
                        {t('motherName') || "Mother's Name"}
                      </label>
                      <input
                        id="motherName"
                        name="motherName"
                        type="text"
                        placeholder="Mother's Name"
                        value={formData.motherName}
                        onChange={handleChange}
                      />
                    </div>
                  </li>
                  <li className="w_1_3-2">
                    <div className="formInpt">
                      <label className="mb_10" htmlFor="gender" data-i18n="gender">
                        {t('gender') || 'Gender'}
                      </label>
                      <select
                        id="gender"
                        name="gender"
                        className="personnel-native-select"
                        value={formData.gender}
                        onChange={handleChange}
                      >
                        <option value="">Select Gender</option>
                        <option value="Male" data-i18n="male">{t('male') || 'Male'}</option>
                        <option value="Female" data-i18n="female">{t('female') || 'Female'}</option>
                      </select>
                    </div>
                  </li>
                  <li className="w_1_3-2">
                    <div className="formInpt hasDatePicker">
                      <label className="mb_10" htmlFor="birthDate" data-i18n="birthDate">
                        {t('birthDate') || 'Birth Date'}
                      </label>
                      <input
                        type="text"
                        id="birthDate"
                        name="birthDate"
                        className="customDatepicker"
                        placeholder='Date Of Birth'
                        value={formData.birthDate}
                        onChange={handleChange}
                      />
                    </div>
                  </li>
                  <li className="w_1_3">
                    <div className="formInpt">
                      <label className="mb_10" htmlFor="placeOfBirth" data-i18n="placeOfBirth">
                        {t('placeOfBirth') || "Place of Birth"}
                      </label>
                      <input
                        id="placeOfBirth"
                        name="placeOfBirth"
                        type="text"
                        placeholder="Place of Birth"
                        value={formData.placeOfBirth}
                        onChange={handleChange}
                      />
                    </div>
                  </li>
                  <li className="w_1_3">
                    <div className="formInpt">
                      <label className="mb_10" htmlFor="phone" data-i18n="phone">
                        {t('phone') || 'Phone'}
                      </label>
                      <input
                        id="phone"
                        name="phone"
                        type="tel"
                        placeholder="456-xxxx-xxxx"
                        value={formData.phone}
                        onChange={handleChange}
                        style={invalidFieldStyle('phone', fieldErrors)}
                      />
                      <FieldError name="phone" fieldErrors={fieldErrors} />
                    </div>
                  </li>
                  <li className="w_1_3">
                    <div className="formInpt">
                      <label className="mb_10" htmlFor="status" data-i18n="status">
                        {t('status') || 'Status'}
                      </label>
                      <select
                        id="status"
                        name="status"
                        className="personnel-native-select"
                        value={formData.status}
                        onChange={handleChange}
                      >
                        <option value="">Select Status</option>
                        <option value="heggan">Heggan</option>
                        <option value="fakad">Fakad</option>
                        <option value="dhaawac">Dhaawac</option>
                        <option value="hawlgab">Hawlgab</option>
                        <option value="naafo">Naafo</option>
                      </select>
                    </div>
                  </li>
                  <li className="w_1_3">
                    <div className="formInpt">
                      <label className="mb_10" htmlFor="educationalLevel" data-i18n="educationalLevel">
                        {t('educationalLevel') || 'Educational Level'}
                      </label>
                      <select
                        id="educationalLevel"
                        name="educationalLevel"
                        className="personnel-native-select"
                        value={formData.educationalLevel}
                        onChange={handleChange}
                      >
                        <option value="" data-i18n="SelectEducationalLevel">Select Educational Level</option>
                        <option value="N/A">N/A</option>
                        <option value="Farbarasho">Farbarasho</option>
                        <option value="Dugsi Hoose">Dugsi Hoose</option>
                        <option value="DugsiDhexe">DugsiDhexe</option>
                        <option value="Dugsisare">Dugsisare</option>
                        <option value="machad">machad</option>
                        <option value="Jaamacad">Jaamacad</option>
                      </select>
                    </div>
                  </li>
                  <li className="w_1_3-2">
                    <div className="formInpt">
                      <label className="mb_10" htmlFor="maritalStatus" data-i18n="maritalStatus">
                        {t('maritalStatus') || 'Marital Status'}
                      </label>
                      <select
                        id="maritalStatus"
                        name="maritalStatus"
                        className="personnel-native-select"
                        value={formData.maritalStatus}
                        onChange={handleChange}
                      >
                        <option value="" data-i18n="SelectMaritalStatus">Select Marital Status</option>
                        <option value="Single" data-i18n="single">{t('single') || 'Single'}</option>
                        <option value="Married" data-i18n="married">{t('married') || 'Married'}</option>
                      </select>
                    </div>
                  </li>
                  <li className="w_1_3-2">
                    <div className="formInpt">
                      <label className="mb_10" htmlFor="bloodType" data-i18n="bloodType">
                        {t('bloodType') || 'Blood Type'}
                      </label>
                      <select
                        id="bloodType"
                        name="bloodType"
                        className="personnel-native-select"
                        value={formData.bloodType}
                        onChange={handleChange}
                      >
                        <option value="" data-i18n="SelectBloodType">Select Blood Type</option>
                        <option value="N/A">N/A</option>
                        <option value="O+">O+</option>
                        <option value="O-">O-</option>
                        <option value="A+">A+</option>
                        <option value="A-">A-</option>
                        <option value="B+">B+</option>
                        <option value="B-">B-</option>
                        <option value="AB+">AB+</option>
                        <option value="AB-">AB-</option>
                      </select>
                    </div>
                  </li>


                  <li className="w_1_3">
                    <div className="formInpt">
                      <label className="mb_10" htmlFor="email" data-i18n="email">
                        {t('email') || 'Email'}
                      </label>
                      <input
                        id="email"
                        name="email"
                        type="email"
                        placeholder="name@domain"
                        value={formData.email}
                        onChange={handleChange}
                        style={invalidFieldStyle('email', fieldErrors)}
                      />
                      <FieldError name="email" fieldErrors={fieldErrors} />
                    </div>
                  </li>
                  <li className="w_1_3">
                    <div className="formInpt">
                      <label className="mb_10" htmlFor="armyType" data-i18n="armyType">
                        {t('armyType') || 'Army Type'}
                      </label>
                      <select
                        id="armyType"
                        name="armyType"
                        className=""
                        value={formData.armyType}
                        onChange={handleChangeArmyType}
                        style={invalidFieldStyle('armyType', fieldErrors)}
                      >
                        <option value="">Select Army Type</option>
                        {armyTypes.map(type => (
                          <option key={type.id} value={type.id}>{type.name}</option>
                        ))}
                      </select>
                      <FieldError name="armyType" fieldErrors={fieldErrors} />
                    </div>
                  </li>
                  <li className="w_1_3">
                    <div className="formInpt">
                      <label className="mb_10" htmlFor="armyDepartment" data-i18n="armyDepartment">
                        {t('armyDepartment') || 'Army Department'}
                      </label>
                      <select
                        id="armyDepartment"
                        name="armyDepartment"
                        className=""
                        value={formData.armyDepartment}
                        onChange={handleChangeArmyType}
                        disabled={!formData.armyType}
                        style={invalidFieldStyle('armyDepartment', fieldErrors)}
                      >
                        <option value="">{formData.armyType ? 'Select Department' : '-- Select Army Type First --'}</option>
                        {filteredDepartments.map(dept => (
                          <option key={dept.id} value={dept.id}>{dept.somali_name || dept.name}</option>
                        ))}
                      </select>
                      <FieldError name="armyDepartment" fieldErrors={fieldErrors} />
                    </div>
                  </li>
                  <li className="w_1_3">
                    <div className="formInpt">
                      <label className="mb_10" htmlFor="subDepartment" data-i18n="subDepartment">
                        {t('subDepartment') || 'Sub Department'}
                      </label>
                      <select
                        id="subDepartment"
                        name="subDepartment"
                        className=""
                        value={formData.subDepartment}
                        onChange={handleChangeArmyType}
                        disabled={!formData.armyDepartment}
                      >
                        <option value="">{formData.armyDepartment ? 'Select Sub Department' : '-- Select Department First --'}</option>
                        {filteredSubDepartments.map(subDept => (
                          <option key={subDept.id} value={subDept.id}>{subDept.somali_name || subDept.name}</option>
                        ))}
                      </select>
                    </div>
                  </li>
                  <li className="w_1_3">
                    <div className="formInpt">
                      <label className="mb_10" htmlFor="organization" data-i18n="organization">
                        {t('organization') || 'Organization'}
                      </label>
                      <input name="organization" type="text" value={formData.organization} onChange={handleChange} />
                    </div>
                  </li>
                  <li className="w_1_3">
                    <div className="formInpt">
                      <label className="mb_10" htmlFor="rank" data-i18n="rank">
                        {t('armyRank') || 'Army Rank'}
                      </label>
                      <select
                        id="rank"
                        name="rank"
                        className="personnel-native-select"
                        value={formData.rank}
                        onChange={handleChange}
                        style={invalidFieldStyle('rank', fieldErrors)}
                      >
                        <option value="" data-i18n="SelectArmyRank">{t('SelectArmyRank', 'Select Army Rank')}</option>
                        {ranks.map(r => {
                          const val = r.name || r;
                          const label = r.somali_name ? `${r.name} (${r.somali_name})` : (r.name || r);
                          return (
                            <option key={r.id || val} value={val}>
                              {label}
                            </option>
                          );
                        })}
                      </select>
                      <FieldError name="rank" fieldErrors={fieldErrors} />
                    </div>
                  </li>
                  <li className="w_1_3">
                    <div className="formInpt">
                      <label className="mb_10" htmlFor="title" data-i18n="title">
                        {t('title') || 'Title'}
                      </label>
                      <input name="title" type="text" value={formData.title} onChange={handleChange} />
                    </div>
                  </li>
                  <li className="w_1_3">
                    <div className="formInpt">
                      <label className="mb_10" htmlFor="salaryUsd" data-i18n="salary">
                        {t('salary') || 'Salary (USD)'}
                      </label>
                      <input
                        id="salaryUsd"
                        name="salaryUsd"
                        type="text"
                        placeholder="Managed centrally via Payroll"
                        value={formData.salaryUsd}
                        readOnly={true}
                        disabled={true}
                        style={{ backgroundColor: '#f8f9fa', cursor: 'not-allowed' }}
                      />
                    </div>
                  </li>
                  <li className="w_1_3">
                    <div className="formInpt">
                      <label className="mb_10" htmlFor="allowanceUsd" data-i18n="allowance">
                        {t('allowance') || 'Allowance (USD)'}
                      </label>
                      <input
                        id="allowanceUsd"
                        name="allowanceUsd"
                        type="text"
                        placeholder="Managed centrally via Payroll"
                        value={formData.allowanceUsd}
                        readOnly={true}
                        disabled={true}
                        style={{ backgroundColor: '#f8f9fa', cursor: 'not-allowed' }}
                      />
                    </div>
                  </li>
                  <li className="w_1_3">
                    <div className="formInpt">
                      <label className="mb_10" htmlFor="bankName">
                        Bank Name
                      </label>
                      <select
                        id="bankName"
                        name="bankName"
                        className="customSelect"
                        value={formData.bankName}
                        onChange={handleChange}
                      >
                        <option value="">-- Select Bank Name --</option>
                        <option value="PUNTLAND STATE BANK">PUNTLAND STATE BANK</option>
                        <option value="SALAAM BANK">SALAAM BANK</option>
                        <option value="DAHABSHIIL BANK">DAHABSHIIL BANK</option>
                        <option value="AMAL BANK">AMAL BANK</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                  </li>
                  <li className="w_1_3">
                    <div className="formInpt">
                      <label className="mb_10" htmlFor="bankAccount">
                        Bank Account Number
                      </label>
                      <input
                        id="bankAccount"
                        name="bankAccount"
                        type="text"
                        placeholder="e.g. 1009876543"
                        value={formData.bankAccount}
                        onChange={handleChange}
                      />
                    </div>
                  </li>
                  <li className="w_1_1">
                    <div className="formInpt">
                      <label className="mb_10" htmlFor="address" data-i18n="address">
                        {t('address') || 'Address'}
                      </label>
                      <input
                        id="address"
                        name="address"
                        type="text"
                        placeholder="City, District"
                        value={formData.address}
                        onChange={handleChange}
                      />
                    </div>
                  </li>
                  <li className="w_1_3">
                    <div className="formInpt">
                      <label className="mb_10" htmlFor="training" data-i18n="training">
                        {t('training') || 'Training'}
                      </label>
                      <input
                        id="training"
                        name="training"
                        type="text"
                        placeholder="Training Details"
                        value={formData.training}
                        onChange={handleChange}
                      />
                    </div>
                  </li>
                  <li className="w_1_3">
                    <div className="formInpt">
                      <label className="mb_10" htmlFor="armyEquipment" data-i18n="armyEquipment">
                        {t('armyEquipment') || 'Army Equipment'}
                      </label>
                      <input
                        id="armyEquipment"
                        name="armyEquipment"
                        type="text"
                        placeholder="Army Equipment"
                        value={formData.armyEquipment}
                        onChange={handleChange}
                      />
                    </div>
                  </li>
                  <li className="w_1_3">
                    <div className="formInpt">
                      <label className="mb_10" htmlFor="remarks" data-i18n="remarks">
                        {t('remarks') || 'Remarks'}
                      </label>
                      <input
                        id="remarks"
                        name="remarks"
                        type="text"
                        placeholder="Remarks"
                        value={formData.remarks}
                        onChange={handleChange}
                      />
                    </div>
                  </li>
                  <li className="w_1_3">
                    <div className="formInpt">
                      <label className="mb_10" htmlFor="previousData" data-i18n="previousData">
                        {t('previousData') || 'Previous Data'}
                      </label>
                      <select
                        id="previousData"
                        name="previousData"
                        className="personnel-native-select"
                        value={formData.previousData}
                        onChange={handleChange}
                      >
                        <option value="" data-i18n="SelectPreviousData">Select Previous Data</option>
                        <option value="Haa">Haa</option>
                        <option value="Maya">Maya</option>
                      </select>
                    </div>
                  </li>
                  <li className="w_1_3">
                    <div className="formInpt">
                      <label className="mb_10" htmlFor="workPlace" data-i18n="workPlace">
                        {t('workPlace') || 'Work Place'}
                      </label>
                      <input
                        id="workPlace"
                        name="workPlace"
                        type="text"
                        placeholder="Work Place"
                        value={formData.workPlace}
                        onChange={handleChange}
                      />
                    </div>
                  </li>
                </ul>
              </div>
            </div>

            <div className="mb_18 formBlock">
              <h2 className="txt20 fwMedium colorBlack" data-i18n="additionalInfo">
                {t('additionalInfo') || 'Additional Information'}
              </h2>
              <ul className="dFlex pt_8 pb_8 formFieldsWrap">
                <li className="w_1_3">
                  <div className="formInpt">
                    <label className="mb_10" htmlFor="emergencyContact" data-i18n="emergencyContact">
                      {t('emergencyContact') || 'Emergency Name'}
                    </label>
                    <input
                      id="emergencyContact"
                      name="emergencyContact"
                      type="text"
                      placeholder="Name"
                      value={formData.emergencyContact}
                      onChange={handleChange}
                    />
                  </div>
                </li>
                <li className="w_1_3">
                  <div className="formInpt">
                    <label className="mb_10" htmlFor="emergencyContactPhone" data-i18n="phone">
                      {t('Contact') || 'Contact'}
                    </label>
                    <input
                      id="emergencyContactPhone"
                      name="emergencyContactPhone"
                      type="tel"
                      placeholder="456-xxxx-xxxx"
                      value={formData.emergencyContactPhone}
                      onChange={handleChange}
                    />
                  </div>
                </li>
                <li className="w_1_3">
                  <div className="formInpt">
                    <label className="mb_10" htmlFor="emergencyContactRelation" data-i18n="relationship">
                      {t('relationship') || 'Relation'}
                    </label>
                    <input
                      id="emergencyContactRelation"
                      name="emergencyContactRelation"
                      type="text"
                      placeholder="Relation"
                      value={formData.emergencyContactRelation}
                      onChange={handleChange}
                    />
                  </div>
                </li>

                <li className="w_1_3">
                  <div className="formInpt">
                    <label className="mb_10" htmlFor="responsiblePersonName" data-i18n="fullName">
                      {t('responsiblePerson') || 'Responsible Person'}
                    </label>
                    <input
                      id="responsiblePersonName"
                      name="responsiblePersonName"
                      type="text"
                      placeholder="Name"
                      value={formData.responsiblePersonName}
                      onChange={handleChange}
                    />
                  </div>
                </li>
                <li className="w_1_3">
                  <div className="formInpt">
                    <label className="mb_10" htmlFor="responsiblePersonPhone" data-i18n="contact">
                      {t('contact') || 'Contact'}
                    </label>
                    <input
                      id="responsiblePersonPhone"
                      name="responsiblePersonPhone"
                      type="tel"
                      placeholder="456-xxxx-xxxx"
                      value={formData.responsiblePersonPhone}
                      onChange={handleChange}
                    />
                  </div>
                </li>
              </ul>
            </div>

            <div className="mb_18 formBlock">
              <h2 className="txt20 fwMedium colorBlack" data-i18n="registeredLocation">
                {t('registeredLocation') || 'Registered Location'}
              </h2>
              <ul className="dFlex pt_8 pb_8 formFieldsWrap">
                <li className="w_1_3">
                  <div className="formInpt">
                    <label className="mb_10" htmlFor="region" data-i18n="region">
                      {t('region') || 'Region'}
                    </label>
                    <select name="region" value={formData.region} onChange={handleChangeRegion} style={invalidFieldStyle('region', fieldErrors)}>
                      <option value="">Select Region</option>
                      {regions.map(region => (
                        <option key={region} value={region}>{region}</option>
                      ))}
                    </select>
                    <FieldError name="region" fieldErrors={fieldErrors} />
                  </div>
                </li>
                <li className="w_1_3">
                  <div className="formInpt">
                    <label className="mb_10" htmlFor="district" data-i18n="district">
                      {t('district') || 'District'}
                    </label>
                    <select name="district" value={formData.district} onChange={handleChangeRegion} disabled={!formData.region} style={invalidFieldStyle('district', fieldErrors)}>
                      <option value="">Select District</option>
                      {filteredDistricts.map(district => (
                        <option key={district} value={district}>{district}</option>
                      ))}
                    </select>
                    <FieldError name="district" fieldErrors={fieldErrors} />
                  </div>
                </li>
                <li className="w_1_3">
                  <div className="formInpt">
                    <label className="mb_10" htmlFor="station" data-i18n="station">
                      {t('station') || 'Station'}
                    </label>
                    <input
                      id="station"
                      name="station"
                      type="text"
                      placeholder="Station Name"
                      value={formData.station}
                      onChange={handleChange}
                      style={invalidFieldStyle('station', fieldErrors)}
                    />
                    <FieldError name="station" fieldErrors={fieldErrors} />
                  </div>
                </li>
                <li className="w_1_3">
                  <div className="formInpt">
                    <label className="mb_10" htmlFor="enrollmentCount" data-i18n="biometricEnrollment">
                      {t('biometricEnrollment') || 'Biometric Enrollment'}
                    </label>
                    <input
                      id="enrollmentCount"
                      name="enrollmentCount"
                      type="text"
                      placeholder="Enrollment Count"
                      value={formData.biometricEnrolledCount}
                      onChange={handleChange}
                    />
                  </div>
                </li>
              </ul>
            </div>

            <div style={{ marginTop: '2rem' }}>
              <button type="submit" disabled={loading} className="btn primary">
                {loading ? 'Adding...' : (t('save') || 'Add Personnel')}
              </button>
              <button
                type="button"
                onClick={() => onNavigate('personnel')}
                className="btn secondary"
                style={{ marginLeft: '1rem' }}
              >
                {t('cancel') || 'Cancel'}
              </button>
            </div>
          </form>
        </div>
      </div>

      {showEnrollModal && (
        <div className="modal" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="modal-content" style={{ background: '#fff', borderRadius: '8px', maxWidth: '600px', width: '100%', padding: '24px' }}>
            <div className="d-flex justify-content-between align-items-center mb-20">
              <h3 style={{ margin: 0 }}>{t('enrollFingerprint') || 'Enroll Fingerprint'}</h3>
              <button type="button" className="btnClose" onClick={closeEnrollModal} style={{ background: 'transparent', border: 'none', fontSize: '1.25rem', cursor: 'pointer' }}>×</button>
            </div>

            <div className="mb_16">
              <p>{t('enrollFingerprintInstructions') || 'Use the connected ZKTeco fingerprint device to capture the fingerprint template. Then save the registration after capture.'}</p>
            </div>

            <div className="mb_16">
              <label className="mb_8">{t('selectBiometricDevice') || 'Select Biometric Device'}</label>
              <select value={selectedBiometricDeviceId} onChange={(e) => setSelectedBiometricDeviceId(e.target.value)} className="form-control">
                <option value="">{t('chooseDevice') || 'Choose device'}</option>
                {availableDevices.map(device => (
                  <option key={device.id} value={device.id}>
                    {device.device_id} - {device.model}
                  </option>
                ))}
              </select>
            </div>

            <div className="mb_16">
              <label className="mb_8">{t('captureStatus') || 'Capture Status'}</label>
              <div style={{ padding: '12px', background: '#f5f5f5', borderRadius: '4px', minHeight: '48px' }}>
                {captureStatus || (t('readyToCapture') || 'Ready to capture fingerprint')}
              </div>
            </div>

            {capturedFingerprintData && (
              <div className="mb_16">
                <div style={{ padding: '16px', borderRadius: '4px', background: '#e8f5e9', borderLeft: '4px solid #4caf50' }}>
                  <p style={{ margin: '0 0 12px 0', fontWeight: 'bold', color: '#2e7d32' }}>✓ {t('fingerprintCaptured') || 'Fingerprint data successfully captured.'}</p>

                  {capturedFingerprintData.image ? (
                    <div style={{ textAlign: 'center', marginBottom: '12px' }}>
                      {capturedFingerprintData.image.startsWith && capturedFingerprintData.image.startsWith('data:') ? (
                        <img
                          src={capturedFingerprintData.image}
                          alt="Captured Fingerprint"
                          style={{
                            maxWidth: '220px',
                            maxHeight: '180px',
                            border: '2px solid #4caf50',
                            borderRadius: '4px',
                            background: '#fff'
                          }}
                        />
                      ) : (
                        (() => {
                          const dims = estimateFingerprintDims(capturedFingerprintData.image);
                          return <FingerprintImageCanvas imageData={capturedFingerprintData.image} width={dims.width} height={dims.height} />
                        })()
                      )}
                      <p style={{ margin: '8px 0 0 0', fontSize: '0.875rem', color: '#555' }}>
                        Fingerprint Image ({capturedFingerprintData.width || capturedFingerprintData.height || 0}×{capturedFingerprintData.height || 0})
                      </p>
                    </div>
                  ) : capturedFingerprintData.template ? (
                    <div style={{ textAlign: 'center', marginBottom: '12px' }}>
                      <p style={{ margin: '8px 0 0 0', fontSize: '0.875rem', color: '#555' }}>
                        Fingerprint Template ({capturedFingerprintData.template.length} bytes)
                      </p>
                    </div>
                  ) : null}

                  <p style={{ margin: 0, fontSize: '0.875rem', color: '#555' }}>
                    {t('fingerprintSavedToLog') || 'The fingerprint template has been captured and will be saved with the personnel record.'}
                  </p>
                </div>
              </div>
            )}

            <div className="d-flex justify-content-end gap-3">
              <button type="button" className="btn secondary" onClick={closeEnrollModal} disabled={captureLoading}>
                {capturedFingerprintData ? (t('close') || 'Close') : (t('cancel') || 'Cancel')}
              </button>
              {!capturedFingerprintData && (
                <button type="button" className="btn primary" onClick={handleFingerprintCapture} disabled={captureLoading || !selectedBiometricDeviceId}>
                  {captureLoading ? (t('capturing') || 'Capturing...') : (t('captureFingerprint') || 'Capture Fingerprint')}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      <ExcelUploadModal
        isOpen={showExcelModal}
        onClose={() => setShowExcelModal(false)}
        onUpload={() => {
          console.log('Personnel import completed successfully');
        }}
        api={api}
      />

      <WebcamCaptureModal
        isOpen={showWebcamModal}
        onClose={() => setShowWebcamModal(false)}
        onCapture={handleWebcamPhotoCapture}
        title={t('capturePersonnelPhoto', 'Capture Personnel Photo')}
      />
    </>
  );
};

export default AddPersonnel;
